import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core'
import { LocationSnapshotService } from '../services/location-snapshot.service';
import { UserCountry } from '../types/UserCountry';
import { SharedService } from 'src/app/services/SharedService';
import { LocationSnapshot } from '../types/LocationSnapshot';
import { LocationSnapshotFilters } from '../types/LocationSnapshotFilters';
import { GlobalIndustrySegment } from '../types/GlobalIndustrySegment';
import { BehaviorSubject, Subscription, forkJoin, interval, take, timer } from 'rxjs';
import { Workbook } from 'exceljs';
import { utils, writeFile } from 'xlsx-js-style';
import { APPlant } from '../types/APPlant';
import { NotificationService } from 'src/app/common/component/notification/notification.service';
import { exportDataGrid, exportPivotGrid } from 'devextreme/excel_exporter';
import { saveAs } from 'file-saver';
import { DemandCapacityComponent } from '../demand-capacity/demand-capacity.component';
import { OpportunitiesComponent } from '../opportunities/opportunities.component';
import { OpenLeadsByPlantComponent } from '../open-leads-by-plant/open-leads-by-plant.component';
import { BatchReportsGridEvent } from '../../batch-reports-grid/batch-reports-grid-types';
import { BatchReportsService } from '../../batch-reports-grid/batch-reports-service';
import { v4 as uuidv4 } from 'uuid';
import { DxAccordionComponent, DxTabPanelComponent } from 'devextreme-angular';
import { Utility } from 'src/app/common/service/utility';
import { TextDataResponse } from 'src/app/common/model/TextDataResponse';
import { Opportunity } from '../types/Opportunity';
import { SuspectListComponent } from '../suspect-list/suspect-list.component';
import { SAPVolumeBetterServedComponent } from '../sap-volume-better-served/sap-volume-better-served.component';
import { SuspectsWithContactNamesComponent } from '../suspects-with-contact-names/suspects-with-contact-names.component';
import { OpenLead } from '../types/OpenLead';
import { SuspectsWithContactNames } from '../types/SuspectsWithContactNames';
import { SuspectList } from '../types/SuspectList';
import { DemandCapacity } from '../types/DemandCapacity';
import { CapacityByPlant } from '../types/CapacityByPlant';
import { ExclusiveCapacityByPlant } from '../types/ExclusiveCapacityByPlant';
import { SAPBetterServedDetails } from '../types/SAPVolumeBetterServed';
import { SuspectMap } from '../types/SuspectMap';
import { SuspectMapComponent } from '../suspect-map/suspect-map.component';
import { toJpeg } from 'html-to-image';
import { SuspectHeatMapComponent } from '../suspect-heat-map/suspect-heat-map.component';
import { DemandByDistance } from '../types/DemandByDistances';
import { DemandByDistanceComponent } from '../demand-by-distance/demand-by-distance.component';
import { SuspectHeatMapService } from '../services/suspect-heatmap.service';
import { SuspectsAltHeatMapComponent } from '../suspects-alt-heat-map/suspects-alt-heat-map.component';
import { SuspectsAltHeatMap } from '../types/SuspectsAltHeatMap';

export class ExportStatus {
  suspectMapCompleted: boolean
  suspectHeatMapCompleted: boolean
  everythingElseCompleted: boolean
  suspectAltHeatMapCompleted: boolean
}

@Component({
  selector: 'location-snapshot-main',
  templateUrl: './main.component.html',
  styleUrls: ['./main.component.scss']
})
export class LocationSnapshotMainComponent implements OnInit {
  @ViewChild('demandCapacity') demandCapacityComponent: DemandCapacityComponent;
  @ViewChild('demandByDistance') demandByDistanceComponent: DemandByDistanceComponent;
  @ViewChild('opportunities') opportunityComponent: OpportunitiesComponent;
  @ViewChild('openLeads') openLeadsComponent: OpenLeadsByPlantComponent;
  @ViewChild('suspectMap') suspectMapComponent: SuspectMapComponent;
  @ViewChild('suspectHeatMap') suspectHeatMapComponent: SuspectHeatMapComponent;
  @ViewChild('suspectAltHeatMap') suspectAltHeatMapComponent: SuspectsAltHeatMapComponent;
  @ViewChild('sapVolumeBetterServed') sapVolumeBetterServedComponent: SAPVolumeBetterServedComponent;
  @ViewChild('suspectList') suspectListComponent: SuspectListComponent;
  @ViewChild('suspectsWithContactNames') suspectsWithContactNamesComponent: SuspectsWithContactNamesComponent;
  @ViewChild('accordion', { static: false }) accordion: DxAccordionComponent;
  @ViewChild('LSTabPanel', { static: false }) tabPanel: DxTabPanelComponent;

  suspectsWithContactNames: SuspectsWithContactNames[] = [];
  isLoadPanelVisible: boolean = false;
  isDataLoaded: boolean = false;
  isCountrySelected: boolean = false;
  tooltipVisible: boolean = false;
  exportButtonVisible: boolean = true;
  globalIndustrySegments: GlobalIndustrySegment[] = [];
  userCountries: UserCountry[] = [];
  apPlants: APPlant[] = [];
  removeBatchGrid: boolean = false;
  opportunitiesJobID: string = '';
  isSuspectListLoaded: boolean = false;
  isSuspectsWithContactNamesLoaded: boolean = false;
  isDemandCapacityLoaded: boolean = false;
  isCapacityByPlantLoaded: boolean = false;
  isAsyncReportLoading: boolean = false;
  isCapacityByPlantExclusiveLoaded: boolean = false;
  isOpportunitiesLoaded: boolean = false;
  isOpenLeadsByPlantLoaded: boolean = false;
  isSAPVolumeBetterServedLoaded: boolean = false;
  isSuspectMapLoaded: boolean = false;
  isSuspectAltHeatMapLoaded: boolean = false;
  isDemandByDistanceLoaded: boolean = false;
  locationSnapshot: LocationSnapshot = {
    filters: new LocationSnapshotFilters,
    demandCapacities: [[], []],
    capacityByPlants: [],
    exclusiveCapacityByPlants: [],
    opportunities: [],
    openLeadsByPlant: [],
    suspectMap: new SuspectMap(),
    suspectHeatMap: '',
    suspectAltHeatMap: new SuspectsAltHeatMap(),
    demandByDistances: [[], [], [], []],
    sapVolumeBetterServed: [],
    suspectList: [],
    suspectsWithContactNames: []
  };

  suspectAltHeatMapLabel: string = 'Suspects Alt Heat Map';
  products = [
    { text: 'LOX/LIN' },
    { text: 'LAR' },
    { text: 'CO2' }
  ];

  locationSnapshotTabs: Reporttab[] = [
    { text: 'Demand Capacity' },
    { text: 'Opportunities' },
    { text: 'Open Leads by Plant' },
    { text: 'Suspect Map' },
    { text: 'Suspect Heat Map' },
    { text: 'Suspect Alt Heat Map' },
    { text: 'Demand by Distance' },
    { text: 'SAP Volume Better Served' },
    { text: 'Suspect List' },
    { text: 'Suspects with Contract Names' },
    { text: 'Parameters' }
  ];

  tabIndexAtExport: number = 0;
  exportStatus: ExportStatus = { suspectMapCompleted: false, suspectHeatMapCompleted: false, everythingElseCompleted: false, suspectAltHeatMapCompleted: false };
  exportStatus$ = new BehaviorSubject<ExportStatus>(this.exportStatus);
  subs: Subscription = new Subscription();

  constructor(private locationSnapshotService: LocationSnapshotService, private sharedSvc: SharedService, private notificationService: NotificationService,
    private batchReportsService: BatchReportsService, private utility: Utility, private changeDetector: ChangeDetectorRef, private suspectHeatMapService: SuspectHeatMapService) {
    this.locationSnapshot.filters.radiusAroundEachPlant = 200;
    this.locationSnapshot.filters.trailerSize = 18;
    this.locationSnapshot.filters.distributionCost = 3.7;
    this.locationSnapshot.filters.siteNames = '';
    this.locationSnapshot.filters.globalSegments = ''
    this.Initialize();
  }

  ngOnInit(): void {
    if (this.locationSnapshot.filters.includeSuspectList === undefined) {
      this.locationSnapshot.filters.includeSuspectList = true;
    }
    if (this.locationSnapshot.filters.removeExcluded === undefined) {
      this.locationSnapshot.filters.removeExcluded = false;
    }
    if (this.locationSnapshot.filters.crossCountryBorders === undefined) {
      this.locationSnapshot.filters.crossCountryBorders = true;
    }
    if (this.locationSnapshot.filters.limitToSiteRevenueGrtThan1MM === undefined) {
      this.locationSnapshot.filters.limitToSiteRevenueGrtThan1MM = false;
    }
    if (this.locationSnapshot.filters.showDistanceRings === undefined) {
      this.locationSnapshot.filters.showDistanceRings = true;
    }
    if (this.locationSnapshot.filters.includeSAPCustomerList === undefined) {
      this.locationSnapshot.filters.includeSAPCustomerList = true;
    }

  }

  private Initialize() {
    this.isLoadPanelVisible = true;
    forkJoin(
      [this.locationSnapshotService.getAllGlobalIndustrySegments(), this.locationSnapshotService.getUserCountries(this.sharedSvc.UserRoles.apexId, this.sharedSvc.UserRoles.isIGAdmin, this.sharedSvc.UserRoles.isIGPower, this.sharedSvc.UserRoles.isIGAmerica, this.sharedSvc.UserRoles.isIGEurope, this.sharedSvc.UserRoles.isIGAsia, false, false)]
    ).subscribe(([globalIndustrySegmentsData, userCountries]) => {
      this.globalIndustrySegments = globalIndustrySegmentsData;
      this.userCountries = userCountries;
      if (this.userCountries === undefined || this.userCountries === null || this.userCountries.length < 1) {
        let errorResponse = {
          error: { Status: "400", Title: "Please contact your power user to have your security set up." }
        };
        this.notificationService.alert("error", "Error reading countries", errorResponse);
      }
      this.isLoadPanelVisible = false;
    }
    );
  }

  OnProductsCreated(e) {
    e.component.content().querySelector('.dx-list-select-all').style.display = 'none'
  }

  loadReports() {
    let that = this;
    this.isLoadPanelVisible = true;
    that.resetDataLoadStatus();
    this.locationSnapshot.filters.plantCompany = "Air Products";
    that.locationSnapshot.filters.siteNames = '';
    that.locationSnapshot.filters.globalSegments = ''
    this.locationSnapshot.filters.siteIDs.forEach(function (item, index) {
      that.locationSnapshot.filters.siteNames += that.apPlants.find(p => p.siteID == item).siteCombined + " + ";
    });
    if (that.locationSnapshot.filters.siteNames.length > 3) {
      that.locationSnapshot.filters.siteNames = that.locationSnapshot.filters.siteNames.slice(0, -3);
    }
    this.locationSnapshot.filters.globalIndustrySegmentIDs.forEach(function (item, index) {
      that.locationSnapshot.filters.globalSegments += that.globalIndustrySegments.find(p => p.globalIndustrySegmentId == item).name + " + ";
    });
    if (that.locationSnapshot.filters.globalSegments.length > 1) {
      that.locationSnapshot.filters.globalSegments = that.locationSnapshot.filters.globalSegments.slice(0, -3);
    }
    var reportId = uuidv4();
    let requests = [
      this.batchReportsService.submitJob(1, reportId, JSON.stringify(this.locationSnapshot.filters)),
      this.batchReportsService.submitJob(2, reportId, JSON.stringify(this.locationSnapshot.filters)),
      this.batchReportsService.submitJob(3, reportId, JSON.stringify(this.locationSnapshot.filters)),
      this.batchReportsService.submitJob(4, reportId, JSON.stringify(this.locationSnapshot.filters)),
      this.batchReportsService.submitJob(5, reportId, JSON.stringify(this.locationSnapshot.filters)),
      this.batchReportsService.submitJob(6, reportId, JSON.stringify(this.locationSnapshot.filters)),
      this.batchReportsService.submitJob(8, reportId, JSON.stringify(this.locationSnapshot.filters)),
      this.batchReportsService.submitJob(9, reportId, JSON.stringify(this.locationSnapshot.filters)),
      this.batchReportsService.submitJob(11, reportId, JSON.stringify(this.locationSnapshot.filters)),
      this.batchReportsService.submitJob(12, reportId, JSON.stringify(this.locationSnapshot.filters))
    ];

    if (this.locationSnapshot.filters.includeSuspectList) {
      requests.push(this.batchReportsService.submitJob(10, reportId, JSON.stringify(this.locationSnapshot.filters)));
    }

    forkJoin(
      requests
    ).subscribe(() => {
      this.isLoadPanelVisible = false;
      this.reloadBatchGrid();
      this.notificationService.alert("success", `The request to generate the reports is submitted. Please click the "Refresh" button to check the report's status or visit this page after some time.`, null);
    }
    );
  }

  loadPanelVisible() {
    this.isLoadPanelVisible = true;
  }

  onBatchGridEvent($event: BatchReportsGridEvent) {
    let that = this;
    that.resetDataLoadStatus();
    that.isAsyncReportLoading = true;
    that.loadPanelVisible();
    that.cleanupBeforeReportLoad();
    if ($event.data?.jobs.length > 0) {
      this.locationSnapshot.filters.siteNames = '';
      this.batchReportsService.getAPIBody($event.data?.jobs[0].jobID).subscribe({
        next: async (data) => {
          that.locationSnapshot.filters = data;
          $event.data?.jobs.forEach(element => {
            this.getDemandCapacity(element, that);
            this.getCapacityByPlant(element, that);
            this.getCapacityByPlantExclusive(element, that);
            this.getOpportunities(element, that);
            this.getOpenLeadsByPlant(element, that);
            this.getSAPVolumeBetterServed(element, that);
            this.getSuspectsWithContactNames(element, that);
            if (this.locationSnapshot.filters.includeSuspectList) {
              this.getSuspectList(element, that);
            } else {
              this.isSuspectListLoaded = true;
            }
            this.getSuspectMap(element, that);
            this.getDemandByDistance(element, that);
            this.getSuspectAltHeatMap(element, that);
          });
        },
        error: (error: any) => {
          this.notificationService.alert("error", `Error while retrieving API Body`, error);
        },
      });
    }
  }

  cleanupBeforeReportLoad() {
    this.locationSnapshot.suspectMap = new SuspectMap();
    this.locationSnapshot.suspectAltHeatMap = new SuspectsAltHeatMap();
  }

  private checkAllDataLoaded() {
    if (this.isSuspectListLoaded && this.isSuspectsWithContactNamesLoaded && this.isDemandCapacityLoaded
      && this.isCapacityByPlantLoaded && this.isCapacityByPlantExclusiveLoaded && this.isOpportunitiesLoaded
      && this.isOpenLeadsByPlantLoaded && this.isSAPVolumeBetterServedLoaded && this.isSuspectMapLoaded && this.isDemandByDistanceLoaded) {
      this.isDataLoaded = true;
      this.isLoadPanelVisible = false;
      this.accordion.instance.collapseItem(0);
      this.isAsyncReportLoading = false;
    }

  }

  private resetDataLoadStatus() {

    this.isDataLoaded = false;

    this.isSuspectListLoaded = false;
    this.isSuspectsWithContactNamesLoaded = false;
    this.isDemandCapacityLoaded = false;
    this.isCapacityByPlantLoaded = false;
    this.isCapacityByPlantExclusiveLoaded = false;
    this.isOpportunitiesLoaded = false;
    this.isOpenLeadsByPlantLoaded = false;
    this.isSAPVolumeBetterServedLoaded = false;
    this.isSuspectMapLoaded = false;
    this.isDemandByDistanceLoaded = false;

    this.tabPanel.selectedIndex = 0;
  }

  private getDemandByDistance(element: any, that: this) {
    if (element.reportSubType === 'Demand by Distance') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.demandByDistances = data;
            this.isDemandByDistanceLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Demand By Distance' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        this.isDemandByDistanceLoaded = true;
      }
    }
  }

  private getSAPVolumeBetterServed(element: any, that: this) {
    if (element.reportSubType === 'SAP Volume Better Served') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.sapVolumeBetterServed = data;
            this.isSAPVolumeBetterServedLoaded = true;
            this.checkAllDataLoaded();
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'SAP Volume Better Served' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      }
      else {
        this.isSAPVolumeBetterServedLoaded = true;
      }

    }
  }

  private getOpenLeadsByPlant(element: any, that: this) {
    if (element.reportSubType === 'Open Leads by Plant') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.openLeadsByPlant = data;
            this.isOpenLeadsByPlantLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Open Leads by Plant' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        this.isOpenLeadsByPlantLoaded = true;
      }
    }
  }

  private getOpportunities(element: any, that: this) {
    if (element.reportSubType === 'Opportunities') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.opportunities = data;
            this.isOpportunitiesLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Opportunities' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        this.isOpportunitiesLoaded = true;
      }

    }
  }

  private getCapacityByPlantExclusive(element: any, that: this) {
    if (element.reportSubType === 'Capacity By Plant Exclusive') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.exclusiveCapacityByPlants = data;
            this.isCapacityByPlantExclusiveLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Capacity By Plant Exclusive ( Demand Capacity )' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        this.isCapacityByPlantExclusiveLoaded = true;
      }

    }
  }

  private getCapacityByPlant(element: any, that: this) {
    if (element.reportSubType === 'Capacity By Plant') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.capacityByPlants = data;
            this.isCapacityByPlantLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Capacity By Plant ( Demand Capacity )' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        this.isCapacityByPlantLoaded = true;
      }
    }
  }

  private getDemandCapacity(element: any, that: this) {
    if (element.reportSubType === 'Demand Capacity') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.demandCapacities = data;
            this.isDemandCapacityLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Demand Capacity' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        this.isDemandCapacityLoaded = true;
      }
    }
  }

  private getSuspectList(element: any, that: this) {
    if (element.reportSubType === 'Suspect List') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.suspectList = data;
            this.isSuspectListLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Suspect List' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        this.isSuspectListLoaded = true;
      }

    }
  }

  private getSuspectsWithContactNames(element: any, that: this) {
    if (element.reportSubType === 'Suspects with Contact Names') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.suspectsWithContactNames = data;
            that.isSuspectsWithContactNamesLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Suspects With Contact Names' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        that.isSuspectsWithContactNamesLoaded = true;
      }

    }
  }

  private getSuspectMap(element: any, that: this) {
    if (element.reportSubType === 'Suspect Map') {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.suspectMap = data;
            that.isSuspectMapLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Suspect Map' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        that.isSuspectMapLoaded = true;
      }

    }
  }

  private getSuspectAltHeatMap(element: any, that: this) {
    if (element.reportSubType === this.suspectAltHeatMapLabel) {
      if (element.status === 'Completed') {
        this.batchReportsService.getJobOutput(element.jobID).subscribe({
          next: async (data) => {
            that.locationSnapshot.suspectAltHeatMap = data;
            that.isSuspectAltHeatMapLoaded = true;
          },
          error: (error: any) => {
            this.notificationService.alert("error", `Error while retrieving 'Suspect Map' report data`, error);
          },
          complete: () => {
            this.checkAllDataLoaded();
          }
        });
      } else {
        that.isSuspectAltHeatMapLoaded = true;
      }

    }
  }

  exportGrids(e) {

    const workbook = new Workbook();

    this.isLoadPanelVisible = true;

    //Before export, switch to Suspect Map tab (if not switched by user)
    //This is because, the google map image tiles are lazy-loaded ONLY when the map is scrolled into view.
    //If the user never scrolled all the way down, but does an export, then we only get blank images during export.
    //So as a work-around, we do a programmatic switch before export.    
    this.tabIndexAtExport = this.tabPanel.selectedIndex;
    if (this.tabIndexAtExport != 3) {
      this.tabPanel.selectedIndex = 3;
    }

    const context = this;

    const demandCapacitySheet = workbook.addWorksheet('Demand Capacity');
    const opportunitySheet = workbook.addWorksheet('Opportunity');
    const openLeadsSheet = workbook.addWorksheet('OpenLeadsByPlant');
    const suspectMapSheet = workbook.addWorksheet('Suspect Map');
    const suspectHeatMapSheet = workbook.addWorksheet('Suspects Heat Map');
    const suspectAltHeatMapSheet = workbook.addWorksheet(this.suspectAltHeatMapLabel);
    const demandByDistanceSheet = workbook.addWorksheet('Demand by Distance');
    const sapVolumeBetterServedSheet = workbook.addWorksheet('SAP Volume Better Served');
    //const suspectListSheet = workbook.addWorksheet('SuspectList');
    const suspectsWithContactNamesSheet = workbook.addWorksheet('SuspectsWithContactNames');
    const parametersSheet = workbook.addWorksheet('Parameters');
    parametersSheet.columns = [
      { width: 10 }, { width: 50 }, { width: 100 }
    ];

    let suspectHeatMapExport_initiated = false;
    let suspectAltHeatMapExport_initiated = false;

    //We subscribe to export status events. 
    //When suspect map, suspect-heat-map worksheets are ready and all other worksheets are ready, then we write to workbook and download.
    this.subs.unsubscribe();
    this.subs = this.exportStatus$.subscribe(exportStatus => {

      //Initiate suspect heat-map export only after suspect-map export is completed and make sure to initiate only once.
      //This is because for map image export, the map tab has to be in active view, if we switch tab while export is in progress,
      //then we will get a blank image.
      if (exportStatus.suspectMapCompleted && !exportStatus.suspectHeatMapCompleted && !suspectHeatMapExport_initiated) {
        this.initiateExport_SuspectHeatMap(workbook, suspectHeatMapSheet);
        suspectHeatMapExport_initiated = true;
      }

      if (exportStatus.suspectHeatMapCompleted && !exportStatus.suspectAltHeatMapCompleted && !suspectAltHeatMapExport_initiated) {
        this.initiateExport_SuspectAltHeatMap(workbook, suspectAltHeatMapSheet);
        suspectAltHeatMapExport_initiated = true;
      }

      //if all exports are completed, then download the workbook.
      if (exportStatus.suspectMapCompleted
        && exportStatus.suspectHeatMapCompleted
        && exportStatus.suspectAltHeatMapCompleted
        && exportStatus.everythingElseCompleted) {

        workbook.xlsx.writeBuffer()
          .then(function (buffer: BlobPart) {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'LocationSnapshot.xlsx');
          }).then(() => {

            //Begin the export of Suspect List
            timer(3000).subscribe(() => {
              this.exportSuspectList();
            });

          });
        //Reset export status (if exported once more, we don't want this observer to think that it is ready)
        this.exportStatus.suspectMapCompleted = false;
        this.exportStatus.suspectHeatMapCompleted = false;
        this.exportStatus.suspectAltHeatMapCompleted = false;
        this.exportStatus.everythingElseCompleted = false;
      }
    });

    //Initiate suspect-map export.
    //Delay Suspect Map export by 10 seconds for above tab switch and map image loading to complete.
    timer(10000).subscribe(() => {
      this.exportSuspectMap(workbook, suspectMapSheet);
    });

    this.setDemandCapacityHeaders(demandCapacitySheet)
    this.setDemandByDistanceHeaders(demandByDistanceSheet)
    this.setOpportunityHeaders(opportunitySheet);
    this.setOpenLeadsByPlantHeaders(openLeadsSheet);
    this.setSAPVolumeBetterServedHeaders(sapVolumeBetterServedSheet);
    //this.setSuspectListHeaders(suspectListSheet);
    this.setSuspectsWithContactNamesHeaders(suspectsWithContactNamesSheet);
    this.setParameters(parametersSheet);

    function setAlternatingRowsBackground(gridCell, excelCell) { // TBD
      /*if (gridCell.rowType === 'header' || gridCell.rowType === 'data') {
        if (excelCell.fullAddress.row % 2 === 0) {
          excelCell.fill = {
            type: 'pattern', pattern: 'darkVertical', fgColor: { argb: 'D3D3D3' }, bgColor: { argb: 'D3D3D3' },
          };
        }
      }*/
    }

    exportDataGrid({
      worksheet: demandCapacitySheet,
      component: this.demandCapacityComponent.demandCapacitySnapshotGrid.instance,
      topLeftCell: { row: 9, column: 2 },
      customizeCell: ({ gridCell, excelCell }) => {
        setAlternatingRowsBackground(gridCell, excelCell);
        if (gridCell.rowType === 'group') {
          excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
          this.convertStringToNumber(excelCell);
        }
        if (gridCell.rowType === 'totalFooter' && excelCell.value) {
          excelCell.font.italic = true;
        }
        if (gridCell.data === 'Global Industry Segment') {
          excelCell.fill = { fgColor: { argb: '00a8e1' }, bgColor: { argb: 'BEDFE6' } };
        }
      },
    }).then((cellRange) => {
      let headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 2);
      headerRow.getCell(2).value = this.demandCapacityComponent.header12;
      headerRow.getCell(2).font = { bold: true, size: 14, color: { argb: '009a49' } };
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 1);
      headerRow.font = { size: 12, color: { argb: '00a8e1' } };
      headerRow.getCell(2).font = { size: 12, color: { argb: 'FF0000' } };
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 1);
      headerRow.font = { size: 12, color: { argb: '00a8e1' } };
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 1);
      headerRow.font = { size: 12, color: { argb: '00a8e1' } };
    }).then(() => exportDataGrid({
      worksheet: demandCapacitySheet,
      component: this.demandCapacityComponent.demandCapacitySnapshotNonAPGrid.instance,
      topLeftCell: { row: demandCapacitySheet.rowCount - 2, column: 2 },
      customizeCell: ({ gridCell, excelCell }) => {
        setAlternatingRowsBackground(gridCell, excelCell);
        if (gridCell.rowType === 'group') {
          excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
          this.convertStringToNumber(excelCell);
        }
        if (gridCell.rowType === 'totalFooter' && excelCell.value) {
          excelCell.font.italic = true;
        }
      },
    })).then((cellRange) => {
      let headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 3);
      headerRow.getCell(2).value = this.demandCapacityComponent.header7;
      headerRow.getCell(2).font = { bold: true, size: 14, color: { argb: '009a49' } };
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 1);
      headerRow.font = { size: 12, color: { argb: '00a8e1' } };
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 1);
      headerRow.font = { size: 12, color: { argb: '00a8e1' } };
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 1);
      headerRow.font = { size: 12, color: { argb: '00a8e1' } };
    }).then(() => exportDataGrid({
      worksheet: demandCapacitySheet,
      component: this.demandCapacityComponent.demandCapacitySnapshotByCountryGrid.instance,
      topLeftCell: { row: demandCapacitySheet.rowCount - 2, column: 2 },
      customizeCell: ({ gridCell, excelCell }) => {
        setAlternatingRowsBackground(gridCell, excelCell);
        if (gridCell.rowType === 'group') {
          excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
          this.convertStringToNumber(excelCell);
        }
        if (gridCell.rowType === 'totalFooter' && excelCell.value) {
          excelCell.font.italic = true;
        }
      },
    })).then((cellRange) => {
      // header
      let headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 3);
      headerRow.getCell(2).value = this.demandCapacityComponent.header8;
      headerRow.getCell(2).font = { bold: true, size: 14, color: { argb: '009a49' } };
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 1);
      headerRow.getCell(2).value = this.demandCapacityComponent.header9;
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 2);
      headerRow.getCell(2).value = this.demandCapacityComponent.header10;
      headerRow.getCell(2).font = { bold: true, size: 14, color: { argb: '009a49' } };
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 1);
      headerRow.font = { size: 12, color: { argb: '00a8e1' } };
    }).then(() => exportDataGrid({
      worksheet: demandCapacitySheet,
      component: this.demandCapacityComponent.demandCapacityByPlantGrid.instance,
      topLeftCell: { row: demandCapacitySheet.rowCount, column: 2 },
      customizeCell: ({ gridCell, excelCell }) => {
        setAlternatingRowsBackground(gridCell, excelCell);
        if (gridCell.rowType === 'group') {
          excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
          this.convertStringToNumber(excelCell);
        }
        if (gridCell.rowType === 'totalFooter' && excelCell.value) {
          excelCell.font.italic = true;
        }
      },
    })).then((cellRange) => {
      let headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 3);
      headerRow.getCell(2).value = this.demandCapacityComponent.header11;
      headerRow.getCell(2).font = { bold: true, size: 14, color: { argb: '009a49' } };
      headerRow = demandCapacitySheet.getRow(demandCapacitySheet.rowCount + 1);
      headerRow.font = { size: 12, color: { argb: '00a8e1' } };
    })
      .then(() => exportDataGrid({
        worksheet: demandCapacitySheet,
        component: this.demandCapacityComponent.demandCapacityByPlantExclusiveGrid.instance,
        topLeftCell: { row: demandCapacitySheet.rowCount, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
            this.convertStringToNumber(excelCell);
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }
        },
      }))
      .then(() => exportDataGrid({
        worksheet: opportunitySheet,
        component: this.opportunityComponent.opportunitiesGrid.instance,
        topLeftCell: { row: 5, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }
          if ((gridCell.column.dataField === 'createdDate' || gridCell.column.dataField === 'closingDate' ||
            gridCell.column.dataField === 'onstreamDate') && excelCell._row.number > 5) {
            excelCell.numFmt = 'mm/dd/yyyy';
            excelCell.value = this.dateStringToDate(excelCell.value);
          }
        },
      }))
      .then(() => exportDataGrid({
        worksheet: openLeadsSheet,
        component: this.openLeadsComponent.openLeadsGrid.instance,
        topLeftCell: { row: 5, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }

          if ((gridCell.column.dataField === 'createdOn' || gridCell.column.dataField === 'leadSince') && excelCell._row.number > 5) {
            excelCell.numFmt = 'mm/dd/yyyy';
            excelCell.value = this.dateStringToDate(excelCell.value);
          }
        },
      }))
      .then(() => exportDataGrid({
        worksheet: demandByDistanceSheet,
        component: this.demandByDistanceComponent.totalPotentialLOXLINVolumeGrid.instance,
        topLeftCell: { row: 4, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
            this.convertStringToNumber(excelCell);
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }
        },
      }))
      .then((cellRange) => {
        let headerRow = demandByDistanceSheet.getRow(demandByDistanceSheet.rowCount + 2);
        headerRow.getCell(2).value = this.demandByDistanceComponent.header3;
        headerRow.getCell(2).font = { bold: true, size: 14, color: { argb: '009a49' } };
        headerRow = demandByDistanceSheet.getRow(demandByDistanceSheet.rowCount + 1);
        headerRow.getCell(2).value = this.demandByDistanceComponent.header4;
        headerRow = demandByDistanceSheet.getRow(demandByDistanceSheet.rowCount + 1);
        headerRow.font = { size: 12, color: { argb: '00a8e1' } };
      })
      .then(() => exportDataGrid({
        worksheet: demandByDistanceSheet,
        component: this.demandByDistanceComponent.potentialLOXLINVolumeGrid.instance,
        topLeftCell: { row: demandByDistanceSheet.rowCount, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
            this.convertStringToNumber(excelCell);
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }
        },
      }))
      .then((cellRange) => {
        let headerRow = demandByDistanceSheet.getRow(demandByDistanceSheet.rowCount + 2);
        headerRow.getCell(2).value = this.demandByDistanceComponent.header5;
        headerRow.getCell(2).font = { bold: true, size: 14, color: { argb: '009a49' } };
        headerRow = demandByDistanceSheet.getRow(demandByDistanceSheet.rowCount + 1);
        headerRow.font = { size: 12, color: { argb: '00a8e1' } };
      })
      .then(() => exportDataGrid({
        worksheet: demandByDistanceSheet,
        component: this.demandByDistanceComponent.totalPotentialLOXLINVolumeNonAPGrid.instance,
        topLeftCell: { row: demandByDistanceSheet.rowCount, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
            this.convertStringToNumber(excelCell);
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }
        },
      }))
      .then((cellRange) => {
        let headerRow = demandByDistanceSheet.getRow(demandByDistanceSheet.rowCount + 2);
        headerRow.getCell(2).value = this.demandByDistanceComponent.header6;
        headerRow.getCell(2).font = { bold: true, size: 14, color: { argb: '009a49' } };
        headerRow = demandByDistanceSheet.getRow(demandByDistanceSheet.rowCount + 1);
        headerRow.font = { size: 12, color: { argb: '00a8e1' } };
      })
      .then(() => exportDataGrid({
        worksheet: demandByDistanceSheet,
        component: this.demandByDistanceComponent.potentialLOXLINVolumeNonAPGrid.instance,
        topLeftCell: { row: demandByDistanceSheet.rowCount, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
            this.convertStringToNumber(excelCell);
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }
        },
      }))
      .then(() => exportDataGrid({
        worksheet: sapVolumeBetterServedSheet,
        component: this.sapVolumeBetterServedComponent.sapVolumeBetterServedGrid.instance,
        topLeftCell: { row: 6, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }
        },
      }))
      /* .then(() => exportDataGrid({
        worksheet: suspectListSheet,
        component: this.suspectListComponent.suspectListGrid.instance,
        topLeftCell: { row: 6, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }
        },
      })) */
      .then(() => exportDataGrid({
        worksheet: suspectsWithContactNamesSheet,
        component: this.suspectsWithContactNamesComponent.suspectsWithContactNamesGrid.instance,
        topLeftCell: { row: 4, column: 2 },
        customizeCell: ({ gridCell, excelCell }) => {
          setAlternatingRowsBackground(gridCell, excelCell);
          if (gridCell.rowType === 'group') {
            excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BEDFE6' } };
          }
          if (gridCell.rowType === 'totalFooter' && excelCell.value) {
            excelCell.font.italic = true;
          }
        }
      }))
      .then(() => {
        //Finally we send the event that the above worksheets are filled.
        this.exportStatus.everythingElseCompleted = true;
        this.exportStatus$.next(this.exportStatus);

      });
  }

  exportSuspectMapOnly() {

    this.isLoadPanelVisible = true;

    const workbook = new Workbook();
    const suspectMapSheet = workbook.addWorksheet('Suspect Map');

    this.exportSuspectMap(workbook, suspectMapSheet);
    this.subs.unsubscribe();
    this.subs = this.exportStatus$.subscribe(exportStatus => {

      if (exportStatus.suspectMapCompleted) {
        this.exportStatus.suspectMapCompleted = false;
        workbook.xlsx.writeBuffer()
          .then(function (buffer: BlobPart) {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'LocationSnapshot-SuspectMap.xlsx');
          }).then(() => {
            this.isLoadPanelVisible = false;
          });
      }

    });

  }

  exportSuspectHeatMapOnly() {

    this.isLoadPanelVisible = true;

    const workbook = new Workbook();
    const suspectHeatMapSheet = workbook.addWorksheet('Suspect Heat Map');

    this.initiateExport_SuspectHeatMap(workbook, suspectHeatMapSheet);
    this.subs.unsubscribe();
    this.subs = this.exportStatus$.subscribe(exportStatus => {

      if (exportStatus.suspectHeatMapCompleted) {
        this.exportStatus.suspectHeatMapCompleted = false;
        workbook.xlsx.writeBuffer()
          .then(function (buffer: BlobPart) {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'LocationSnapshot-SuspectHeatMap.xlsx');
          }).then(() => {
            this.isLoadPanelVisible = false;
          });
      }

    });

  }

  exportAltSuspectHeatMapOnly() {
    this.isLoadPanelVisible = true;

    const workbook = new Workbook();
    const suspectAltHeatMapSheet = workbook.addWorksheet(this.suspectAltHeatMapLabel);
    this.initiateExport_SuspectAltHeatMap(workbook, suspectAltHeatMapSheet);

    this.subs.unsubscribe();
    this.subs = this.exportStatus$.subscribe(exportStatus => {

      if (exportStatus.suspectAltHeatMapCompleted) {
        this.exportStatus.suspectAltHeatMapCompleted = false;
        workbook.xlsx.writeBuffer()
          .then(function (buffer: BlobPart) {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'LocationSnapshot-SuspectAltHeatMap.xlsx');
          }).then(() => {
            this.isLoadPanelVisible = false;
          });
      }

    });
  }

  private setDemandCapacityHeaders(demandCapacitySheet) {
    demandCapacitySheet.getRow(2).getCell(2).value = this.demandCapacityComponent.header1;
    demandCapacitySheet.getRow(2).getCell(2).font = { bold: true, size: 16, underline: 'double', color: { argb: '009a49' } };
    demandCapacitySheet.getRow(3).getCell(2).value = this.demandCapacityComponent.header2;
    demandCapacitySheet.getRow(3).getCell(2).font = { color: { argb: 'FF0000' } };
    demandCapacitySheet.getRow(4).getCell(2).value = this.demandCapacityComponent.header3;
    demandCapacitySheet.getRow(5).getCell(2).value = this.demandCapacityComponent.header4;
    demandCapacitySheet.getRow(6).getCell(2).value = this.demandCapacityComponent.header5;
    demandCapacitySheet.getRow(8).getCell(2).value = this.demandCapacityComponent.header6;
    demandCapacitySheet.getRow(8).getCell(2).font = { bold: true, size: 14, color: { argb: '009a49' } };
    demandCapacitySheet.getRow(9).font = { size: 12, color: { argb: '00a8e1' } };
    demandCapacitySheet.getRow(10).font = { size: 12, color: { argb: '00a8e1' } };
    demandCapacitySheet.getRow(11).font = { size: 12, color: { argb: '00a8e1' } };
  }

  private setDemandByDistanceHeaders(demandByDistanceSheet) {
    demandByDistanceSheet.getRow(2).getCell(2).value = this.demandByDistanceComponent.header1;
    demandByDistanceSheet.getRow(2).getCell(2).font = { bold: true, size: 16, underline: 'double', color: { argb: '009a49' } };
    demandByDistanceSheet.getRow(3).getCell(2).value = this.demandByDistanceComponent.header2;
    demandByDistanceSheet.getRow(3).getCell(2).font = { color: { argb: 'FF0000' } };
    demandByDistanceSheet.getRow(4).font = { size: 12, color: { argb: '00a8e1' } };
  }

  private setOpportunityHeaders(opportunitySheet) {
    opportunitySheet.getRow(2).getCell(2).value = this.opportunityComponent.header;
    opportunitySheet.getRow(2).getCell(2).font = { bold: true, size: 16, underline: 'double', color: { argb: '009a49' } };
    opportunitySheet.getRow(5).font = { size: 12, color: { argb: '00a8e1' } };
  }

  private setOpenLeadsByPlantHeaders(openLeadsSheet) {
    openLeadsSheet.getRow(2).getCell(2).value = this.openLeadsComponent.header;
    openLeadsSheet.getRow(2).getCell(2).font = { bold: true, size: 16, underline: 'double', color: { argb: '009a49' } };
    openLeadsSheet.getRow(5).font = { size: 12, color: { argb: '00a8e1' } };
  }

  private setSuspectMapHeaders(suspectMapSheet) {
    let all_headers = this.suspectMapComponent.all_headers;

    if (!all_headers) return;

    //Set header row 1
    let hdr1_format = { 'size': 10, 'color': { 'argb': '1C3A70' }, 'name': 'Tahoma' };
    suspectMapSheet.mergeCells('A1:M1');
    suspectMapSheet.getCell('A1').value = {
      'richText': [
        { 'font': hdr1_format, 'text': all_headers.header1_line1 + '\n' },
        { 'font': hdr1_format, 'text': all_headers.header1_line2 }
      ]
    };

    suspectMapSheet.getCell('A1').alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true
    }

    suspectMapSheet.getRow(1).height = 45;

    //Set header row 2
    suspectMapSheet.mergeCells('A2:M2');
    suspectMapSheet.getCell('A2').value = {
      'richText': [
        { 'font': { 'size': 8, 'color': { 'argb': 'FF0000' }, 'name': 'Arial' }, 'text': all_headers.header2 },
      ]
    };
    suspectMapSheet.getCell('A2').alignment = { vertical: 'top', horizontal: 'center', wrapText: true }

    //Set header row 3
    let hdr3_format = { 'size': 10, 'color': { 'argb': '000000' }, 'name': 'Arial' };
    suspectMapSheet.mergeCells('A3:M3');
    suspectMapSheet.getCell('A3').value = {
      'richText': [
        { 'font': hdr3_format, 'text': all_headers.header3_line1 + '\n' },
        { 'font': hdr3_format, 'text': all_headers.header3_line2 }
      ]
    };

    suspectMapSheet.getCell('A3').alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true
    }

    suspectMapSheet.getRow(3).height = 30;
  }

  private setSuspectHeatMapHeaders(suspectHeatMapSheet) {
    let all_headers = this.suspectHeatMapComponent.all_headers;

    if (!all_headers) return;

    //Set header row 1
    let hdr1_format = { 'size': 13, 'color': { 'argb': '1C3A70' }, 'name': 'Tahoma', 'bold': false };
    suspectHeatMapSheet.mergeCells('A1:Q1');
    suspectHeatMapSheet.getCell('A1').value = {
      'richText': [
        { 'font': hdr1_format, 'text': all_headers.header1_line1 }
      ]
    };

    suspectHeatMapSheet.getCell('A1').alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true
    }

    suspectHeatMapSheet.getRow(1).height = 37;

    //Set header row 2
    suspectHeatMapSheet.mergeCells('A2:Q2');
    suspectHeatMapSheet.getCell('A2').value = {
      'richText': [
        { 'font': { 'size': 8, 'color': { 'argb': 'FF0000' }, 'name': 'Arial' }, 'text': all_headers.header2 },
      ]
    };
    suspectHeatMapSheet.getCell('A2').alignment = { vertical: 'top', horizontal: 'center', wrapText: true }

    //Set header row 3
    let hdr3_format = { 'size': 10, 'color': { 'argb': '000000' }, 'name': 'Arial' };
    suspectHeatMapSheet.mergeCells('A3:Q3');
    suspectHeatMapSheet.getCell('A3').value = {
      'richText': [
        { 'font': hdr3_format, 'text': all_headers.header3_line1 + '\n' },
        { 'font': hdr3_format, 'text': all_headers.header3_line2 }
      ]
    };

    suspectHeatMapSheet.getCell('A3').alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true
    }

    suspectHeatMapSheet.getRow(3).height = 60;

    suspectHeatMapSheet.getRow(4).height = 0.1;

    
      
    suspectHeatMapSheet.mergeCells(`A4:Q4`);
    suspectHeatMapSheet.getCell(`A4`).value = {
      'richText': [
        {
          'font': {
            'size': 14, 'color': { 'argb': '1C3A70' },
            'name': 'Tahoma'
          }, 'text': all_headers.header5
        },
      ]
    };

    suspectHeatMapSheet.getCell(`A4`).alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true
    }

    suspectHeatMapSheet.getCell(`A4`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'f2f2f2' },
    };

    suspectHeatMapSheet.getRow(4).height = 40;


    suspectHeatMapSheet.getRow(5).height = 0.1;
    suspectHeatMapSheet.getRow(6).height = 0.1;
    suspectHeatMapSheet.getRow(7).height = 0.1;
  }

  private setSuspectAltHeatMapHeaders(suspectHeatMapSheet) {
    let all_headers = this.suspectHeatMapComponent.all_headers;

    if (!all_headers) return;

    //Set header row 1
    let hdr1_format = { 'size': 13, 'color': { 'argb': '1C3A70' }, 'name': 'Tahoma', 'bold': false };
    suspectHeatMapSheet.mergeCells('A1:E1');
    suspectHeatMapSheet.getCell('A1').value = {
      'richText': [
        { 'font': hdr1_format, 'text': all_headers.header1_line1 }
      ]
    };

    suspectHeatMapSheet.getCell('A1').alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true
    }

    suspectHeatMapSheet.getRow(1).height = 37;

    //Set header row 2
    suspectHeatMapSheet.mergeCells('A2:E2');
    suspectHeatMapSheet.getCell('A2').value = {
      'richText': [
        { 'font': { 'size': 8, 'color': { 'argb': 'FF0000' }, 'name': 'Arial' }, 'text': all_headers.header2 },
      ]
    };
    suspectHeatMapSheet.getCell('A2').alignment = { vertical: 'top', horizontal: 'center', wrapText: true }

    //Set header row 3
    let hdr3_format = { 'size': 10, 'color': { 'argb': '000000' }, 'name': 'Arial' };
    suspectHeatMapSheet.mergeCells('A3:E3');
    suspectHeatMapSheet.getCell('A3').value = {
      'richText': [
        { 'font': hdr3_format, 'text': all_headers.header3_line1 + '\n' },
        { 'font': hdr3_format, 'text': all_headers.header3_line2 }
      ]
    };

    suspectHeatMapSheet.getCell('A3').alignment = {
      vertical: 'top',
      horizontal: 'left',
      wrapText: true
    }

    suspectHeatMapSheet.getRow(3).height = 40;

    suspectHeatMapSheet.mergeCells(`A4:E4`);
    suspectHeatMapSheet.getCell(`A4`).value = {
      'richText': [
        {
          'font': {
            'size': 14, 'color': { 'argb': '1C3A70' },
            'name': 'Tahoma'
          }, 'text': all_headers.header5
        },
      ]
    };

    suspectHeatMapSheet.getCell(`A4`).alignment = {
      vertical: 'middle',
      horizontal: 'center',
      wrapText: true
    }

    suspectHeatMapSheet.getCell(`A4`).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'f2f2f2' },
    };

    suspectHeatMapSheet.getRow(4).height = 20;

    suspectHeatMapSheet.getRow(5).height = 0.1;
    suspectHeatMapSheet.getRow(6).height = 0.1;
    suspectHeatMapSheet.getRow(7).height = 0.1;
  }

  private setSAPVolumeBetterServedHeaders(sapVolumeBetterServedSheet) {
    sapVolumeBetterServedSheet.getRow(2).getCell(2).value = this.suspectListComponent.header1;
    sapVolumeBetterServedSheet.getRow(2).getCell(2).font = { bold: true, size: 16, underline: 'double', color: { argb: '009a49' } };
    sapVolumeBetterServedSheet.getRow(3).getCell(2).value = this.suspectListComponent.header2;
    sapVolumeBetterServedSheet.getRow(3).getCell(2).font = { color: { argb: 'FF0000' } };
    sapVolumeBetterServedSheet.getRow(4).getCell(2).value = this.suspectListComponent.header3;
    sapVolumeBetterServedSheet.getRow(6).font = { size: 12, color: { argb: '00a8e1' } };
  }

  private setSuspectListHeaders(suspectListSheet) {
    suspectListSheet.getRow(2).getCell(2).value = this.suspectListComponent.header1;
    suspectListSheet.getRow(2).getCell(2).font = { bold: true, size: 16, underline: 'double', color: { argb: '009a49' } };
    suspectListSheet.getRow(3).getCell(2).value = this.suspectListComponent.header2;
    suspectListSheet.getRow(3).getCell(2).font = { color: { argb: 'FF0000' } };
    suspectListSheet.getRow(4).getCell(2).value = this.suspectListComponent.header3;
    suspectListSheet.getRow(6).font = { size: 12, color: { argb: '00a8e1' } };
  }

  private setSuspectsWithContactNamesHeaders(suspectsWithContactNamesSheet) {
    suspectsWithContactNamesSheet.getRow(2).getCell(2).value = this.suspectsWithContactNamesComponent.header1;
    suspectsWithContactNamesSheet.getRow(2).getCell(2).font = { bold: true, size: 16, underline: 'double', color: { argb: '009a49' } };
    suspectsWithContactNamesSheet.getRow(4).font = { size: 12, color: { argb: '00a8e1' } };
  }

  private setParameters(parametersSheet) {
    parametersSheet.getRow(2).getCell(2).value = "Parameters Used For Generating the Reports";
    parametersSheet.getRow(2).getCell(2).font = { bold: true, size: 16, underline: 'double', color: { argb: '009a49' } };
    parametersSheet.getRow(3).getCell(2).value = "Report Parameters";
    parametersSheet.getRow(3).getCell(2).width = 50;
    parametersSheet.getRow(3).getCell(2).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(3).getCell(3).value = "Value";
    parametersSheet.getRow(3).getCell(3).width = 100;
    parametersSheet.getRow(3).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(3).font = { bold: true, size: 12, color: { argb: '00a8e1' } };
    parametersSheet.getRow(4).getCell(2).value = "Who/When";
    parametersSheet.getRow(4).getCell(3).value = this.demandCapacityComponent.header3;
    parametersSheet.getRow(4).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(5).getCell(2).value = "Country";
    parametersSheet.getRow(5).getCell(3).value = this.locationSnapshot.filters.countryIDs;
    parametersSheet.getRow(5).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(6).getCell(2).value = "AP Plants";
    parametersSheet.getRow(6).getCell(3).value = this.locationSnapshot.filters.siteNames;
    parametersSheet.getRow(6).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(7).getCell(2).value = "Global Industry Segments";
    parametersSheet.getRow(7).getCell(3).value = this.locationSnapshot.filters.globalSegments;
    parametersSheet.getRow(7).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(8).getCell(2).value = "Radius around each plant ( miles )";
    parametersSheet.getRow(8).getCell(3).value = this.locationSnapshot.filters.radiusAroundEachPlant;
    parametersSheet.getRow(8).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(9).getCell(2).value = "Include Suspect List";
    parametersSheet.getRow(9).getCell(3).value = this.locationSnapshot.filters.includeSuspectList;
    parametersSheet.getRow(9).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(10).getCell(2).value = "Remove Excluded";
    parametersSheet.getRow(10).getCell(3).value = this.locationSnapshot.filters.removeExcluded;
    parametersSheet.getRow(10).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(11).getCell(2).value = "Cross Country Borders";
    parametersSheet.getRow(11).getCell(3).value = this.locationSnapshot.filters.crossCountryBorders;
    parametersSheet.getRow(11).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(12).getCell(2).value = "Limit to SiteRevenue>$1MM";
    parametersSheet.getRow(12).getCell(3).value = this.locationSnapshot.filters.limitToSiteRevenueGrtThan1MM;
    parametersSheet.getRow(12).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(13).getCell(2).value = "Show Distance Rings";
    parametersSheet.getRow(13).getCell(3).value = this.locationSnapshot.filters.showDistanceRings;
    parametersSheet.getRow(13).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(14).getCell(2).value = "Include SAP Customer List";
    parametersSheet.getRow(14).getCell(3).value = this.locationSnapshot.filters.includeSAPCustomerList;
    parametersSheet.getRow(14).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(15).getCell(2).value = "Trailer Size ( SAP Volume Better Served )";
    parametersSheet.getRow(15).getCell(3).value = this.locationSnapshot.filters.trailerSize;
    parametersSheet.getRow(15).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
    parametersSheet.getRow(16).getCell(2).value = "Distribution Cost ( SAP Volume Better Served )";
    parametersSheet.getRow(16).getCell(3).value = this.locationSnapshot.filters.distributionCost;
    parametersSheet.getRow(16).getCell(3).alignment = { vertical: 'middle', horizontal: 'center' };
  }

  reloadBatchGrid() {
    this.removeBatchGrid = true;
    this.changeDetector.detectChanges();
    // change detection should remove the component now
    // then we can enable it again to create a new instance
    this.removeBatchGrid = false;
  }

  onCountrySelectionChanged() {
    var countryIDs: string = '';
    if (this.locationSnapshot.filters.countryIDs !== undefined && this.locationSnapshot.filters.countryIDs !== null && this.locationSnapshot.filters.countryIDs.length > 0) {
      this.isCountrySelected = true;
      this.isLoadPanelVisible = true;
      this.locationSnapshot.filters.countryIDs.forEach((item) => {
        countryIDs = countryIDs + "'" + item + "',";
      })
      if (countryIDs !== null) {
        countryIDs = countryIDs.slice(0, countryIDs.length - 1);
      }
      this.locationSnapshotService.getAPPlantsByCountry(countryIDs, this.sharedSvc.UserRoles.isIGPower)
        .subscribe(
          (data: APPlant[]) => {
            this.apPlants = data;
            if (!this.isAsyncReportLoading) {
              this.isLoadPanelVisible = false;
            }
          }
        );
    } else {
      this.isCountrySelected = false;
    }

  }

  exportSuspectMap(workbook: any, suspectMapSheet: any) {

    exportPivotGrid({
      worksheet: suspectMapSheet,
      component: this.suspectMapComponent.suspectsMapCustNearPlantGrid.instance,
      topLeftCell: { row: 39, column: 1 },
      customizeCell: ({ pivotCell, excelCell }) => {
        //Apply border format for all cells in the pivot grid
        let cellBorder = {
          border: {
            top: { style: 'thin', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } }
          }
        };

        Object.assign(excelCell, cellBorder);

        //Apply formatting for each data cell (including grand total cells)
        let dataCellFmt = {
          numFmt: '#,##0',
          alignment: { vertical: 'middle', horizontal: 'right' }
        };

        if (this.isDataCell(pivotCell) || this.isGrandTotalDataCell(pivotCell)) {
          excelCell.value = pivotCell.value ? pivotCell.value as Number : pivotCell.value;
          Object.assign(excelCell, dataCellFmt);
        }
      },
    })
      .then(async (cellRange) => {
        //Pivotgrid export freezes the first few columns. We don't want that, so we unfreeze.
        suspectMapSheet.views = [{ state: 'normal' }];

        //Set headers for Map
        await this.setSuspectMapHeaders(suspectMapSheet);

        // -- Format Pivot Grid -- 
        //Unmerge and delete top header row of grid.
        suspectMapSheet.unMergeCells('A39');
        suspectMapSheet.spliceRows(39, 1);
        suspectMapSheet.getColumn(1).width = 27;

        //Some standard style definitions to re-use further below.
        let stdHeaderFill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '008080' },
        };

        let stdHeaderFont = { name: 'Arial', size: 10, color: { argb: 'ffffff' }, bold: true };

        let cellA39Properties = {
          value: 'Global Industry Segment',
          fill: stdHeaderFill,
          font: stdHeaderFont,
          alignment: { vertical: 'top', horizontal: 'left', wrapText: true }
        }

        Object.assign(suspectMapSheet.getCell('A39'), cellA39Properties);

        let headerCellProperties = {
          fill: stdHeaderFill,
          font: stdHeaderFont,
          alignment: { vertical: 'top', horizontal: 'right', wrapText: true }
        }

        let headerRow = suspectMapSheet.getRow(39);

        for (let i = 2; i <= cellRange.to.column; i++) {
          Object.assign(headerRow.getCell(i), headerCellProperties);
        }

        // -- End Format Pivot Grid -- 

      })
      .then(() => {

        // Export Maps as images.

        //Reset the map view port (pan,zoom etc), if the user had changed the map view.
        //this.suspectMapComponent.adjustSuspectsMapViewport();


        let images: Array<{ imageId: number, type: "map" | "legend", elementId: string }> = new Array();
        let promiseArray: Array<Promise<{ elementId: string, type: "map" | "legend", dataURL: string }>> = new Array();

        //For each map to be exported, convert the map and its legend to image.
        //This call returns promises and we hold them in an array.
        const promise1 = this.convertElementToImage('suspects-map-viewport', "map");
        const promise2 = this.convertElementToImage('suspects-map-legend', "legend");

        promiseArray.push(promise1, promise2);

        //Then we wait for all promises to resolve. The conversion to image takes some time.
        //Each promise resolves to an object of type {elementId: string, type: "map" | "legend", dataURL: string}        
        Promise.all(promiseArray).then((values) => {

          let imageId = 0;

          //Here dataURL is the base64 format of the jpeg image. 
          //Adding image to workbook is a two step process. Fist step, add it to workbook and it gives back an imageId.          
          values.forEach(async obj => {
            imageId = await workbook.addImage({
              base64: obj.dataURL,
              extension: 'jpeg',
            });

            //Store all imageIds in an array, in next step these imageIds will be added to worksheet.
            images.push({ imageId: imageId, elementId: obj.elementId, type: obj.type });
          });

        }).then(() => {
          let tlColMap = 0;
          let brColMap = 15;
          let excelRow_Last = 3;

          //Then add the images one by one to worksheet.
          //In this way 'map' image will be placed first, and corresponding 'legend' image on its side.
          //Then the next 'map' image and 'legend' image and so on.
          images.forEach(image => {

            let tlCol: number, brCol: number;
            let tlRow: number, brRow: number;

            //Determine the positions where the map and legend images need to be placed in Excel.
            if (image.type == 'map') {
              tlCol = tlColMap;
              brCol = brColMap;
              tlRow = excelRow_Last;
              brRow = excelRow_Last + 34;
            } else if (image.type == "legend") {
              tlCol = brColMap + 0.5;
              brCol = brColMap + 4;
              tlRow = excelRow_Last;
              brRow = excelRow_Last + (this.suspectMapComponent.indSegments.size <= 8 ? 11 : 16);
            }

            //Add the image to the worksheet.
            suspectMapSheet.addImage(image.imageId, {
              tl: { col: tlCol, row: tlRow },
              br: { col: brCol, row: brRow }
            });
          });
        }).then(() => {
          //Finally we send the event that the suspects map worksheet is filled.
          this.exportStatus.suspectMapCompleted = true;
          this.exportStatus$.next(this.exportStatus);
        });
      });
  }

  initiateExport_SuspectHeatMap(workbook: any, suspectHeatMapSheet: any) {
    //then switch to suspects heat map tab,
    this.tabPanel.selectedIndex = 4;

    //Reset the map view port (pan,zoom etc), if the user had changed the map view.
    //this.suspectHeatMapComponent.adjustSuspectsMapViewport();

    let mapConfigs = this.suspectHeatMapComponent.mapConfigs;
    //Scroll the maps into view, so that they load.
    interval(3000).pipe(take(mapConfigs.length)).subscribe(i => {
      document.getElementById(mapConfigs[i].legendDivID).scrollIntoView();
    });

    //after 10 seconds, start suspect heat map export.
    timer(10000).subscribe(() => {
      this.exportSuspectHeatMap(workbook, suspectHeatMapSheet);
    });
  }

  async exportSuspectHeatMap(workbook: any, suspectHeatMapSheet: any) {

    await this.setSuspectHeatMapHeaders(suspectHeatMapSheet);

    let mapConfigs = this.suspectHeatMapComponent.mapConfigs;

    let images: Array<{ imageId: number, type: "map" | "legend", elementId: string }> = new Array();
    let promiseArray: Array<Promise<{ elementId: string, type: "map" | "legend", dataURL: string }>> = new Array();

    //For each map to be exported, convert the map and its legend to image.
    //This call returns promises and we hold them in an array.
    mapConfigs.forEach(map => {

      const promise1 = this.convertElementToImage(map.viewportDivID, "map");
      const promise2 = this.convertElementToImage(map.legendDivID, "legend");

      promiseArray.push(promise1, promise2);

    });

    //Then we wait for all promises to resolve. The conversion to image takes some time.
    //Each promise resolves to an object of type {elementId: string, type: "map" | "legend", dataURL: string}        
    Promise.all(promiseArray).then((values) => {

      let imageId = 0;

      //Here dataURL is the base64 format of the jpeg image. 
      //Adding image to workbook is a two step process. Fist step, add it to workbook and it gives back an imageId.          
      values.forEach(async obj => {
        imageId = await workbook.addImage({
          base64: obj.dataURL,
          extension: 'jpeg',
        });

        //Store all imageIds in an array, in next step these imageIds will be added to worksheet.
        images.push({ imageId: imageId, elementId: obj.elementId, type: obj.type });
      });

    }).then(() => {
      let tlColMap = 0;
      let brColMap = 17;
      let excelRow_Last = 3;

      let images_OrderedArray = new Array();

      //We donot know in what order the promises resolved. But for each map, 
      //we need the map image followed by its corresponding legend image. So we order them first, 
      //based on what order they appear in the mapconfig.
      mapConfigs.forEach(map => {
        images_OrderedArray.push(images.find(image => image.elementId == map.viewportDivID));
        images_OrderedArray.push(images.find(image => image.elementId == map.legendDivID));
      });

      //Then add the images one by one to worksheet.
      //In this way 'map' image will be placed first, and corresponding 'legend' image on its side.
      //Then the next 'map' image and 'legend' image and so on.
      images_OrderedArray.forEach(image => {

        let tlCol: number, brCol: number;
        let tlRow: number, brRow: number;

        //Add the display name above each map.
        if (image.type == 'map' && image.elementId == 'suspects-heatmap2-viewport') {

          let mapHdrRowNum = excelRow_Last + 3;
          suspectHeatMapSheet.mergeCells(`A${mapHdrRowNum}:Q${mapHdrRowNum}`);
          suspectHeatMapSheet.getCell(`A${mapHdrRowNum}`).value = {
            'richText': [
              {
                'font': {
                  'size': 14, 'color': { 'argb': '1C3A70' },
                  'name': 'Tahoma'
                }, 'text': mapConfigs.find(map => map.viewportDivID == image.elementId).displayName
              },
            ]
          };

          suspectHeatMapSheet.getCell(`A${mapHdrRowNum}`).alignment = {
            vertical: 'middle',
            horizontal: 'center',
            wrapText: true
          }

          suspectHeatMapSheet.getCell(`A${mapHdrRowNum}`).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'f2f2f2' },
          };

          suspectHeatMapSheet.getRow(mapHdrRowNum).height = 40;
        }

        //Determine the positions where the map and legend images need to be placed in Excel.
        if (image.type == 'map') {
          tlCol = tlColMap;
          brCol = brColMap;
          tlRow = excelRow_Last + 3;
          brRow = excelRow_Last + 34
        } else if (image.type == "legend") {
          tlCol = brColMap + 0.5;
          brCol = brColMap + 3.5;
          tlRow = excelRow_Last + 3;
          brRow = excelRow_Last + 11.5;
          //We extend the last row number by 34, so next map starts at that position.
          excelRow_Last = excelRow_Last + 34;
        }

        //Add the image to the worksheet.
        suspectHeatMapSheet.addImage(image.imageId, {
          tl: { col: tlCol, row: tlRow },
          br: { col: brCol, row: brRow }
        });
      });

    }).then(() => {
      //Finally we send the event that suspectHeatMap worksheet is filled.
      this.exportStatus.suspectHeatMapCompleted = true;
      this.exportStatus$.next(this.exportStatus);
    });

  }

  //Export Suspect List
  // - Suspect List data grid can be ver large, ranging from 30K to upwards of 130K rows in some cases
  // - ExcelJS does not support exporting so many rows. It runs into memory issues beyond 50K rows.
  // - So for this export alone, we use a library called "xlsx-js-style" which is a Fork of SheetJS.
  //    - This is a fork of SheetJS Community Edition with support for basic styling.
  //    - Sheetjs - https://docs.sheetjs.com/docs/getting-started/examples/export
  //    - xlsx-js-style : https://www.npmjs.com/package/xlsx-js-style?activeTab=readme  
  async exportSuspectList() {

    const workbook = utils.book_new();  //Create new workbook
    const worksheet = utils.json_to_sheet([]);  //Create empty worksheet

    this.suspectListComponent.suspectListGrid.instance  //Grab the underlying data array of the grid
      .getDataSource()
      .store()
      .load()
      .then(async data => {

        //Specify the column order similar to what the user sees in the datagrid. otherwise the column ordering will be based on
        //how the fields appear in the JSON Object.
        var data_headers = ["dunsNumber", "dataSource", "companyName", "address", "city", "countyPrefecture", "province", "zipCode",
          "countryid", "phoneNumber", "territory", "apRefSite", "refMiles", "closestSelectedPlantDistDm", "apSite", "miles",
          "closestAPDistanceKm", "altCo", "altMiles", "apAltDistanceKm", "apToAltAPDistanceMi", "apToAltAPDistanceKm", "altNonAPCo",
          "altNonAPMiles", "nonAPkm", "apAdvg", "apDistanceFavKm", "apDistanceFav", "exclude", "excludeComment", "custComment",
          "locationType", "siteRevenueDollars", "isSiteRevenueOver1MM", "employeeCountTotal", "employeeCountHere", "isSmallBusiness",
          "squareFootage", "yearStarted", "lineOfBusinessPerDB", "isGasUserDisplayText", "isAPAccountDisplayText",
          "isCompetitorAccountDisplayText", "competitorName", "globalIndustrySegment", "siC2Desc", "siC8", "siC8Desc", "naicsCode",
          "naicsCodeDescription", "contact1", "contact2", "contact3", "contact4", "yNProbability", "mtpdN2", "mtpdO2", "mtpdAr",
          "mtpdCO2", "mtpdN2NoAP", "mtpdO2NoAP", "mtpdArNoAP", "mtpdCO2NoAP", "siteID"];

        //Export the entire data array to the worksheet
        await utils.sheet_add_json(worksheet, data, { header: data_headers, origin: 6, skipHeader: true });

        //Remove the unwanted columns which appear at the end. These fields are not part of datagrid, so we should remove them.
        //Below code tells sheetjs that the last column in the range should be BL and not BY, BZ (effectively removing the last 13 or 14 unwanted columns).        
        //Ex: Original worksheet['!ref'] -> A1:BY12345
        //Override it as worksheet['!ref'] -> A1:BL12345
        worksheet['!ref'] = worksheet['!ref'].substring(0, 3) + 'BL' + worksheet['!ref'].substring(5);

      }).then(async () => {

        //Add the header with proper column names as shown in the data grid.
        var new_headers = ["DUNS Number", "Data Source", "Company", "Address", "City", "County Prefecture", "State/Province",
          "Post Code", "Country", "Phone Number", "US LB Territory", "Closest Selected Plant", "Closest Selected Plant dist miles",
          "Closest Selected Plant dist km", "Closest AP Site", "Closest AP Distance miles", "Closest AP Distance km",
          "AP alt Plant", "AP alt Distance mi", "AP alt Distance km", "AP to altAP Distance mi", "AP to altAP Distance km",
          "NonAP Plant", "NonAP mi", "NonAP km", "AP Distance Fav. miles", "AP Distance Fav. km", "Is 'AP Distance Fav.' positive?",
          "Exclude", "Exclude Comment", "Cust Comment", "Location Type", "Site Revenue", "Is Site Revenue Over $1MM",
          "Employee Count Total", "Employee Count Here", "Is Small Business", "Square Footage", "Year Started",
          "Line Of Business Per D&B", "Is Gas User", "Is AP Account", "Is Competitor Account", "Competitor Name",
          "Global Industry Segment", "SIC2 Desc", "SIC8", "SIC8 Desc", "NAICS Code", "NAICS Description", "Contact1", "Contact2",
          "Contact3", "Contact4", "Yes/No Probability", "Relative  LIN Volume by 'mva'", "Relative  LOX Volume by 'mva'",
          "Relative  LAR Volume by 'mva'", "Relative  CO2 Volume by 'mva'", "Relative  LIN Volume by 'mva' no AP",
          "Relative  LOX Volume by 'mva' no AP", "Relative  LAR Volume by 'mva' no AP", "Relative  CO2 Volume by 'mva' no AP",
          "SiteID"];

        let new_headers_with_formatting = [];

        //Apply some formatting each header cell
        new_headers.forEach(headerVal => {
          new_headers_with_formatting.push({
            v: headerVal,
            t: "s",
            s: {
              alignment: { vertical: "top", horizontal: "center", wrapText: true },
              font: { name: "Calibri", bold: true, color: { rgb: "00A8E1" }, sz: 12 }
            }
          });
        });

        //Add the header row
        await utils.sheet_add_aoa(worksheet, [new_headers_with_formatting], { origin: 5 });

        let colHeights = [];

        //Adjust the header column width and row height
        for (let i = 0; i < new_headers.length; i++) {
          colHeights.push({ 'width': 20 });
        }

        worksheet['!cols'] = colHeights;

        worksheet['!rows'] = [, , , , , { 'hpt': 50 }];


      }).then(async () => {

        //Add some workhsheet level headers with some formatting
        let sheet_header1 =
          { v: this.suspectListComponent.header1, t: "s", s: { font: { name: "Calibri", bold: true, color: { rgb: "009A49" }, sz: 16 } } };


        let sheet_header2 =
          { v: this.suspectListComponent.header2, t: "s", s: { font: { name: "Calibri", bold: false, color: { rgb: "FF0000" }, sz: 11 } } };


        let sheet_header3 =
          { v: this.suspectListComponent.header3, t: "s", s: { font: { name: "Calibri", bold: false, color: { rgb: "000000" }, sz: 11 } } };

        await utils.sheet_add_aoa(worksheet, [
          [sheet_header1],
          [sheet_header2],
          [sheet_header3],
        ], { origin: "A2" });
      }).then(async () => {
        //Finally add the worksheet to the workbook and write it to the file
        await utils.book_append_sheet(workbook, worksheet, "SuspectList");
        await writeFile(workbook, "Suspect_List.xlsx", { compression: true });

        //Restore the tab-index and hide the loading panel
        this.tabPanel.selectedIndex = this.tabIndexAtExport;
        this.isLoadPanelVisible = false;
      });
  }

  initiateExport_SuspectAltHeatMap(workbook: any, suspectAltHeatMapSheet: any) {
    //then switch to suspects alt heat map tab, 
    this.tabPanel.selectedIndex = 5;

    let mapConfigs = this.suspectAltHeatMapComponent.mapConfigs;
    //Scroll the maps into view, so that they load.
    interval(3000).pipe(take(mapConfigs.length)).subscribe(i => {
      document.getElementById(mapConfigs[i].legendDivID).scrollIntoView();
    });

    //after 10 seconds, start suspect heat map export.
    timer(10000).subscribe(() => {
      this.exportAltSuspectHeatMap(workbook, suspectAltHeatMapSheet);
    });
  }

  async exportAltSuspectHeatMap(workbook: any, suspectAltHeatMapSheet: any) {
    let that = this;
    let lastgridRow = 0;
    exportDataGrid({
      worksheet: suspectAltHeatMapSheet,
      component: that.suspectAltHeatMapComponent.suspectAltHeatMapGrid.instance,
      topLeftCell: { row: 39, column: 1 },
      customizeCell: ({ gridCell, excelCell }) => {
        let cellBorder = {
          border: {
            top: { style: 'thin', color: { argb: '000000' } },
            left: { style: 'thin', color: { argb: '000000' } },
            bottom: { style: 'thin', color: { argb: '000000' } },
            right: { style: 'thin', color: { argb: '000000' } }
          }
        };

        Object.assign(excelCell, cellBorder);

        if (gridCell.rowType === 'totalFooter' && excelCell.value) {
          let stdTotalFill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'F0E68C' },
          };


          let totalCellProperties = {
            fill: stdTotalFill,
          }

          Object.assign(excelCell, totalCellProperties);
        }

        if (excelCell.row > lastgridRow) {
          lastgridRow = excelCell.row;
        }

        //Apply formatting for each data cell (including grand total cells)
        let dataCellFmt = {
          numFmt: '#,##0',
          alignment: { vertical: 'middle', horizontal: 'right' }
        };



        if (this.isDataCell(gridCell) || this.isGrandTotalDataCell(gridCell)) {
          excelCell.value = gridCell.value ? gridCell.value as Number : gridCell.value;
          Object.assign(excelCell, dataCellFmt);
        }

      },
    }).then(async (cellRange) => {
      suspectAltHeatMapSheet.views = [{ state: 'normal' }];
      this.setSuspectAltHeatMapHeaders(suspectAltHeatMapSheet);
      //Some standard style definitions to re-use further below.
      suspectAltHeatMapSheet.unMergeCells('A39');
      let stdHeaderFill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'ADD8E6' },
      };

      let stdHeaderFont = { name: 'Arial', size: 10, color: { argb: 'ffffff' }, bold: true };

      let headerCellProperties = {
        fill: stdHeaderFill,
        font: stdHeaderFont,
        alignment: { vertical: 'top', horizontal: 'center', wrapText: true }
      }

      let headerRow = suspectAltHeatMapSheet.getRow(39);

      for (let i = 1; i <= cellRange.to.column; i++) {
        Object.assign(headerRow.getCell(i), headerCellProperties);
      }
    }).then(() => {

      //Reset the map view port (pan,zoom etc), if the user had changed the map view.
      //this.suspectHeatMapService.adjustSuspectsMapViewport(this.suspectAltHeatMapComponent.altHeatMap1);
      //this.suspectHeatMapService.adjustSuspectsMapViewport(this.suspectAltHeatMapComponent.altHeatMap2);

      let mapConfigs = this.suspectAltHeatMapComponent.mapConfigs;

      let images: Array<{ imageId: number, type: "map" | "legend", elementId: string }> = new Array();
      let promiseArray: Array<Promise<{ elementId: string, type: "map" | "legend", dataURL: string }>> = new Array();

      //For each map to be exported, convert the map and its legend to image.
      //This call returns promises and we hold them in an array.
      mapConfigs.forEach(map => {

        const promise1 = this.convertElementToImage(map.viewportDivID, "map");
        const promise2 = this.convertElementToImage(map.legendDivID, "legend");

        promiseArray.push(promise1, promise2);

      });

      //Then we wait for all promises to resolve. The conversion to image takes some time.
      //Each promise resolves to an object of type {elementId: string, type: "map" | "legend", dataURL: string}        
      Promise.all(promiseArray).then((values) => {

        let imageId = 0;

        //Here dataURL is the base64 format of the jpeg image. 
        //Adding image to workbook is a two step process. Fist step, add it to workbook and it gives back an imageId.          
        values.forEach(async obj => {
          imageId = await workbook.addImage({
            base64: obj.dataURL,
            extension: 'jpeg',
          });

          //Store all imageIds in an array, in next step these imageIds will be added to worksheet.
          images.push({ imageId: imageId, elementId: obj.elementId, type: obj.type });
        });

      }).then(() => {
        let tlColMap = 0;
        let brColMap = 5;
        let excelRow_Last = 3;

        let images_OrderedArray = new Array();

        //We donot know in what order the promises resolved. But for each map, 
        //we need the map image followed by its corresponding legend image. So we order them first, 
        //based on what order they appear in the mapconfig.
        mapConfigs.forEach(map => {
          images_OrderedArray.push(images.find(image => image.elementId == map.viewportDivID));
          images_OrderedArray.push(images.find(image => image.elementId == map.legendDivID));
        });

        //Then add the images one by one to worksheet.
        //In this way 'map' image will be placed first, and corresponding 'legend' image on its side.
        //Then the next 'map' image and 'legend' image and so on.
        images_OrderedArray.forEach(image => {

          let tlCol: number, brCol: number;
          let tlRow: number, brRow: number;

          //Add the display name above each map.
          if (image.type == 'map' && image.elementId == 'suspects-alt-heatmap2-viewport') {

            excelRow_Last = lastgridRow;
            let mapHdrRowNum = excelRow_Last + 3;
            suspectAltHeatMapSheet.mergeCells(`A${mapHdrRowNum}:E${mapHdrRowNum}`);
            suspectAltHeatMapSheet.getCell(`A${mapHdrRowNum}`).value = {
              'richText': [
                {
                  'font': {
                    'size': 14, 'color': { 'argb': '1C3A70' },
                    'name': 'Tahoma'
                  }, 'text': mapConfigs.find(map => map.viewportDivID == image.elementId).displayName
                },
              ]
            };

            suspectAltHeatMapSheet.getCell(`A${mapHdrRowNum}`).alignment = {
              vertical: 'middle',
              horizontal: 'center',
              wrapText: true
            }

            suspectAltHeatMapSheet.getCell(`A${mapHdrRowNum}`).fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'f2f2f2' },
            };

            suspectAltHeatMapSheet.getRow(mapHdrRowNum).height = 20;
          }

          //Determine the positions where the map and legend images need to be placed in Excel.
          if (image.type == 'map') {
            tlCol = tlColMap;
            brCol = brColMap;
            tlRow = excelRow_Last + 3;
            brRow = excelRow_Last + 34
          } else if (image.type == "legend") {
            tlCol = brColMap + 0.5;
            brCol = brColMap + 3.5;
            tlRow = excelRow_Last + 3;
            brRow = excelRow_Last + 11.5;
            //We extend the last row number by 34, so next map starts at that position.
            excelRow_Last = excelRow_Last + 34;
          }

          //Add the image to the worksheet.
          suspectAltHeatMapSheet.addImage(image.imageId, {
            tl: { col: tlCol, row: tlRow },
            br: { col: brCol, row: brRow }
          });
        });

      }).then(() => {
        //Finally we send the event that suspectHeatMap worksheet is filled.
        this.exportStatus.suspectAltHeatMapCompleted = true;
        this.exportStatus$.next(this.exportStatus);
      });
    });
  }

  isDataCell(cell) {
    return (cell.area === 'data' && cell.rowType === 'D' && cell.columnType === 'D');
  }

  isGrandTotalDataCell(cell) {
    return (cell.area === 'data' && (cell.type === 'GT' || cell.rowType === 'GT' || cell.columnType === 'GT'));
  }

  convertElementToImage(elementId: string, type: "map" | "legend") {
    return toJpeg(document.getElementById(elementId), { skipFonts: true, quality: 0.8, includeQueryParams: true })
      .then(dataUrl => {
        return { elementId: elementId, type: type, dataURL: dataUrl };
      });
  }

  convertStringToNumber(excelCell, colNumber: number = 2) {
    if (!excelCell.value) return;
    if (excelCell._column.number > colNumber) {
      if (!isNaN(excelCell.value.replace(/,/g, ''))) {
        excelCell.value = parseFloat(excelCell.value.replace(/,/g, ''));
        excelCell.numFmt = '#,##0.0';
      }
    }
  }

  dateStringToDate(dateString) {
    try {
      var year = dateString.substring(0, 4);
      var month = dateString.substring(4, 6);
      var day = dateString.substring(6, 8);
      var date = new Date(year, month - 1, day, 0, 0, 0, 0);
      const offset = date.getTimezoneOffset()
      date = new Date(date.getTime() - (offset * 60 * 1000));
      return date;
    } catch (error) {
      return null;
    }
  }
}



export class Reporttab {
  text: string;
}